// Column definitions for the Flow2 inline Goals table. Frequency is merged into the
// Goal Amount column (per Figma) and there is no term-tab grouping. Header labels carry
// the preserved info tooltips (cast to satisfy the shared string-typed `label`).
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { EditableColumnDef } from '../../components/molecules/EditableTable';
import { GoalInlineRow } from './goalInlineHelpers';
import { NameCellEdit, NameCellRead } from './cells/NameCell';
import { PriorityCellEdit, PriorityCellRead } from './cells/PriorityCell';
import { GoalAmountCellEdit, GoalAmountCellRead } from './cells/GoalAmountCell';
import { GoalDateCellEdit, GoalDateCellRead } from './cells/GoalDateCell';
import { PriorityInfoTooltipContent } from './cells/shared';
import { InfoTooltip } from '../../components/molecules';
import { FEATURE_NAMES } from '../../common/constants';
import { checkFFEnabled } from '../../utils';
import { ReactComponent as InfoGoalsIcon } from '../../assets/icons/info-goals-page.svg';

const HeaderLabel = ({ label, tooltip }: { label: string; tooltip?: React.ReactNode }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <span>{label}</span>
        {tooltip ? <InfoTooltip message={tooltip} icon={InfoGoalsIcon} /> : null}
    </Box>
);

// The shared EditableColumnDef types `label` as string; header cells render it as-is, so a
// ReactNode header (label + info tooltip) is safe at runtime and only needs a local cast.
const node = (el: React.ReactNode) => el as unknown as string;

interface UseGoalColumnsArgs {
    duplicateNameError?: string;
    onClearDuplicate?: () => void;
}

export const useGoalColumns = ({
    duplicateNameError,
    onClearDuplicate
}: UseGoalColumnsArgs): EditableColumnDef<GoalInlineRow>[] => {
    const { t } = useTranslation();
    const isNewTooltip = checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428']);
    return useMemo(
        () => [
            {
                key: 'goalName',
                label: t('GOALS_INLINE_COL_NAME'),
                align: 'left',
                width: '31%',
                render: (_v, row) => <NameCellRead row={row} />,
                renderEdit: (ctx) => (
                    <NameCellEdit
                        ctx={ctx}
                        duplicateNameError={duplicateNameError}
                        onClearDuplicate={onClearDuplicate}
                    />
                )
            },
            {
                key: 'goalPriority',
                label: node(<HeaderLabel label={t('GOALS_INLINE_COL_PRIORITY')} tooltip={<PriorityInfoTooltipContent />} />),
                align: 'left',
                width: '13%',
                render: (_v, row) => <PriorityCellRead row={row} />,
                renderEdit: (ctx) => <PriorityCellEdit ctx={ctx} />
            },
            {
                key: 'goalAmount',
                label: node(
                    <HeaderLabel
                        label={t('GOALS_INLINE_COL_GOAL_AMOUNT')}
                        tooltip={
                            isNewTooltip ? (
                                <Trans i18nKey="GOALS_INLINE_GOAL_AMOUNT_TOOLTIP" components={{ strong: <strong />, br: <br /> }} />
                            ) : (
                                <Trans i18nKey="TEXT_GOAL_TARGET_AMOUNT_INFO_TEXT" components={{ strong: <strong />, br: <br /> }} />
                            )
                        }
                    />
                ),
                align: 'left',
                width: '30%',
                render: (_v, row) => <GoalAmountCellRead row={row} />,
                renderEdit: (ctx) => <GoalAmountCellEdit ctx={ctx} />
            },
            {
                key: 'goalDate',
                label: node(
                    <HeaderLabel
                        label={t('GOALS_INLINE_COL_GOAL_DATE')}
                        tooltip={
                            isNewTooltip ? (
                                <Trans i18nKey="GOALS_INLINE_GOAL_DATE_TOOLTIP" components={{ strong: <strong />, br: <br /> }} />
                            ) : (
                                <Trans
                                    i18nKey="GOAL_SUMMARY_TARGET_DATE_TEXT_INFO_TEXT"
                                    components={{ strong: <strong />, br: <br /> }}
                                />
                            )
                        }
                    />
                ),
                align: 'left',
                width: '20%',
                render: (_v, row) => <GoalDateCellRead row={row} />,
                renderEdit: (ctx) => <GoalDateCellEdit ctx={ctx} />
            }
        ],
        [t, duplicateNameError, onClearDuplicate, isNewTooltip]
    );
};
